import { getTRPCClient } from "@repo/api";
import {
  type BookConsultationParams,
  type BookCuratedSessionParams as BookCuratedSessionWireParams,
  bookConsultationInputSchema,
  bookCuratedSessionInputSchema,
  type RescheduleVisitParams,
  rescheduleVisitInputSchema,
  visitIdSchema,
} from "@repo/api/schemas";
import { defineWrite } from "../actions/define-write";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { AppointmentRow } from "../fhir/types";
import { mintExamIds } from "./_caller-identities";
import { optimisticProtocolExams } from "./_exam-optimistic";
import { insertOptimisticAppointment } from "./_optimistic-appointment";
import { patientDomainId } from "./_patient-reference";
import { optimisticVisitExecutionReference } from "./_visit-reference";
import { projectProtocolDefinitionRow } from "./protocol-definition-projection";

/**
 * Shared booking actions. Standalone protocol booking is colocated with its
 * purpose-built protocol-booking view; the remaining actions stay here until
 * their own read workflows have dedicated views.
 *
 * The four ways an appointment comes into existence are described by
 * (ADR-0019). Rescheduling is one of them: it cancels a visit and books its
 * work again, so it belongs beside the bookings rather than among the things a
 * visit does once it exists.
 *
 * There is no `useBookings` here and there never will be: a booking is not a
 * resource. It is the workflow BEFORE an appointment exists, so the only thing
 * this slice owns is the intents that end it. What a visit does once it exists
 * stays in `views/visit.ts`.
 *
 * Availability is not here either. It is a query with no collection behind it —
 * the server materializes candidates on demand and nothing caches them, because
 * a candidate is stale the moment it is handed out.
 */

const appointments = () => getDataStoreClient().getCollection<AppointmentRow, string>("appointment");

function loadedVisit(visitId: string): string {
  if (!appointments().has(visitId)) throw new Error(`Visit '${visitId}' is not loaded`);
  return visitId;
}

/** Intent: "book a consultation". */
export const bookConsultation = defineWrite<BookConsultationParams>({
  optimistic: (params) => {
    const input = bookConsultationInputSchema.parse(params);
    insertOptimisticAppointment(input);
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().booking.bookConsultation.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * What the client needs to draw an optimistic row for a curated session, over
 * and above what the server is told.
 *
 * The server derives patient and orders from the order set, which is the whole
 * point — the old path trusted a client-supplied `caseId` and never checked it.
 * But the caller already has both on screen, and waiting a round trip to draw a
 * row it can already describe is the flicker this avoids. They are declared
 * separately, and never sent, so the trust boundary stays where it is.
 */
export type BookCuratedSessionParams = Omit<BookCuratedSessionWireParams, "examIds"> & {
  patientId: string;
};

/** Intent: "book the clinical work a curation already requested". */
const bookCuratedSessionAction = defineWrite<BookCuratedSessionParams & { examIds: Record<string, string> }>({
  optimistic: (params) => {
    const input = bookCuratedSessionInputSchema.parse(params);
    insertOptimisticAppointment({
      id: input.id,
      patientId: params.patientId,
      candidate: input.candidate,
      orderSetId: params.orderSetId,
      kind: "curated-session",
    });
    const client = getDataStoreClient();
    const group = client.getCollection("requestGroup").get(input.orderSetId);
    if (!group) throw new Error("The booked order set is not loaded");
    // A set built without a protocol has no protocol exams to place.
    const canonical = group.instantiatesCanonical?.[0];
    if (!canonical) return;
    const protocol = client.getCollection("planDefinition").get(canonical);
    if (!protocol) throw new Error("The booked protocol is not loaded");
    for (const exam of optimisticProtocolExams(
      { ...input, patientId: params.patientId },
      projectProtocolDefinitionRow(protocol).examDefinitionUrls,
      [...client.getCollection("activityDefinition").values()],
    )) {
      client.getCollection("observation").insert(exam);
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().booking.bookCuratedSession.mutate(
      {
        id: params.id,
        examIds: params.examIds,
        candidate: params.candidate,
        orderSetId: params.orderSetId,
      },
      options,
    );
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

export function bookCuratedSession(params: BookCuratedSessionParams) {
  return bookCuratedSessionAction({ ...params, examIds: mintExamIds() });
}

export const rescheduleVisit = defineWrite<RescheduleVisitParams>({
  optimistic: (params) => {
    const input = rescheduleVisitInputSchema.parse(params);
    const prior = appointments().get(loadedVisit(input.visitId));
    if (!prior) throw new DomainResultError("This visit is not loaded. Refresh and try again.");
    const patientId = prior.patientId ?? prior.participant.flatMap((part) => patientDomainId(part.actor) ?? [])[0];
    if (!patientId) throw new DomainResultError("This visit has no patient. Refresh and try again.");
    insertOptimisticAppointment({ id: input.id, patientId, candidate: input.candidate, orderSetId: prior.orderSetId });
    appointments().update(input.id, (draft) => {
      draft.appointmentType = prior.appointmentType;
      draft.serviceType = prior.serviceType;
      draft.extension = prior.extension;
      draft.supportingInformation = prior.supportingInformation;
    });
    const client = getDataStoreClient();
    for (const name of ["slot", "booked-slot"] as const) {
      const slots = client.getCollection(name);
      for (const slot of slots.values()) {
        const key = slots.getKeyFromItem(slot);
        if (key.startsWith(`${input.visitId}/`)) slots.delete(key);
      }
    }
    const encounters = client.getCollection("encounter");
    for (const encounter of encounters.values()) {
      if (encounter.visitId !== input.visitId) continue;
      encounters.update(`${input.id}/execution-1`, (draft) => {
        draft.class = encounter.class;
        draft.episodeOfCare = encounter.episodeOfCare;
      });
      encounters.update(encounters.getKeyFromItem(encounter), (draft) => {
        draft.status = "cancelled";
      });
    }
    const exams = client.getCollection("observation");
    for (const exam of exams.values()) {
      if (!exam.examInstance || exam.visitId !== input.visitId) continue;
      exams.update(exams.getKeyFromItem(exam), (draft) => {
        draft.encounter = optimisticVisitExecutionReference(visitIdSchema.parse(input.id));
        draft.visitId = input.id;
      });
    }
    appointments().update(loadedVisit(input.visitId), (draft) => {
      draft.status = "cancelled";
      draft.slot = [];
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().booking.reschedule.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
